import type { CourseInfo } from "./course-detail-page";

interface UniversityHeading {
  title: string;
}

export interface UniversityAttributeSection {
  didYouKnow: string;
  vectorImage: string;
  title: string;
  paragraph: string;
  image: string;
  alt: string;
  universityAttributesTitle: string;
  universityAttributes: CourseInfo[];
}

interface RatingDetails {
  name: string;
  value: number;
  stock: number;
}

interface MainRating {
  title: string;
  value?: number;
  buttonText: string;
}
interface RankingInfoSection {
  ratingDetails: RatingDetails[];
  mainRating: MainRating;
  title: string;
  paragraph: string;
  bgImage: string;
}

interface CourseDetailItem {
  title: string;
  data: string;
}
interface CourseDetails {
  title: string;
  items: CourseDetailItem[];
}

interface MasterRequirement {
  title: string;
  data: string;
}

interface UniversityDetailSection {
  courseDetails: CourseDetails;
  mastersRequirements: MasterRequirement[];
  matersRequiredDocuments: string[];
  bacherlorsRequiredDocuments: string[];
  bordertitle: string;
  borderparagraph: string;
  otherRequirementsTitle: string;
  courseRequirementstitle: string;
  backgroundImage: string;
}
interface Course {
  name: string;
  institute_name: string;
  degreelevel_name: string;
  qs_ranking: number;
  studyMode: string;
  fee?: number;
  duration: string;
  course_language: string;
  logo: string;
}
interface Data {
  img?: string;
  paragraph1?: string;
  paragraph2?: string;
}

interface TabData {
  label: string;
  value: string;
  courses?: Course[];
}

export interface CampusLifeTabData {
  label: string;
  value: string;
  data: Data;
}

interface TrendingTopic {
  title: string;
  posts: number;
}

export const universityHeadingData: UniversityHeading = {
  title: "Yale University",
};

export const universityAttributesData: UniversityAttributeSection = {
  title: "About",
  vectorImage: "/images/course-detail/vector-arrow.png",
  paragraph:
    "Being the third-oldest higher education institute in the United States, Yale University (YU) was established in 1701 as the Collegiate School in New Haven, Connecticut. The school was renamed Yale College in 1718 to honor the school's largest benefactor, Elihu Yale. YU was awarded its first doctorate in 1861 and organized as a university in 1887. It has recognition from the New England Commission of Higher Education since 1929 and accreditation from Association to Advance Collegiate Schools of Business, American Bar Association, and Council of the Section of Legal Education and Admissions to the Bar. It classifies as 'R1: Doctoral Universities – Very high research activity'. Yale University is deemed one of the top-ranking universities for international students in the United States.",
  didYouKnow:
    "Last year 600+ International students took admission in Yale University. Start your study abroad journey today to be amongst them.",
  universityAttributesTitle: "Facts and Figures",
  image: "/images/course-detail/did-you-know.png",
  alt: "Did You Know Illustration",
  universityAttributes: [
    {
      title: "Type",
      icon: "Type",
      value: "Private",
    },
    {
      title: "Total Students",
      icon: "TotalStudents",
      value: "4000",
    },
    {
      title: "International students",
      icon: "InternationalStudents",
      value: "800",
    },
    {
      title: "Total programs",
      icon: "TotalPrograms",
      value: "112",
    },
    {
      title: "Nationalities",
      icon: "Nationality",
      value: "38",
    },
    {
      title: "Total campus",
      icon: "TotalCampus",
      value: "03",
    },
  ],
};

export const univeristyRankingInfoData: RankingInfoSection = {
  bgImage: "/images/course-detail/bg-university-ranking.png",
  title: "University rating",
  paragraph: "This rating is an overall score of Times Course Finder",
  mainRating: {
    title: "Times Rating",
    //   value: 327,
    buttonText: " Based on overall",
  },
  ratingDetails: [
    {
      name: "QS World university ranking",
      value: 75,
      stock: 208,
    },
    {
      name: "Times higher education world ranking",
      value: 60,
      stock: 208,
    },
    {
      name: "US news world ranking",
      value: 50,
      stock: 208,
    },
    {
      name: "Shanghai ranking",
      value: 85,
      stock: 208,
    },
    {
      name: "User ranking",
      value: 40,
      stock: 208,
    },
  ],
};

export const universityDetailData: UniversityDetailSection = {
  otherRequirementsTitle: "Required documents",

  courseRequirementstitle: "Masters",
  backgroundImage: "/images/course-detail/bg-course-requirements.png",
  courseDetails: {
    title: "Bachelors",
    items: [
      {
        title: "SAT",
        data: "1450+",
      },
      {
        title: "IELTS",
        data: "7+",
      },
      {
        title: "TOEFL",
        data: "100+",
      },
    ],
  },

  mastersRequirements: [
    {
      title: "BMAT",
      data: "1450+",
    },
    {
      title: "GRE",
      data: "7+",
    },
    {
      title: "GPA",
      data: "100+",
    },
    {
      title: "IELTS",
      data: "7+",
    },
    {
      title: "TOEFL",
      data: "100+",
    },
  ],
  matersRequiredDocuments: [
    "Proof of English proficiency",
    "Passport identification page",
    "Essay (optional)",
    "Recommendation letter (optional)",
    "Portfolio (if applicable)",
    "Audition (if applicable)",
    "Application form",
    "Application form",
    "Application fee",
  ],
  bacherlorsRequiredDocuments: [
    "Application form",
    "Diploma of previous studies",
    "Passport identification page",
    "Audition (if applicable)",
    "Application fee",
    "Translations",
  ],

  bordertitle: "Entity Requirements",
  borderparagraph:
    "Being the third-oldest higher education institute in the United States, Yale University (YU) was established in 1701 as the Collegiate School in New Haven, Connecticut. The school was renamed Yale College in 1718 to honor the school's largest benefactor, Elihu Yale. YU was awarded its first doctorate in 1861 and organized as a university in 1887. It has recognition from the New England Commission of Higher Education since 1929 and accreditation from Association to Advance Collegiate Schools of Business, American Bar Association, and Council of the Section of Legal Education and Admissions to the Bar. It classifies as 'R1: Doctoral Universities – Very high research activity'. Yale University is deemed one of the top-ranking universities for international students in the United States.",
};

export const browseCourseTabsData: TabData[] = [
  {
    label: "Bachelor",
    value: "bachelors",
    courses: [
      {
        name: "Bachelor of Architectural Engineering (Hons) with Year Abroad",
        institute_name: "Yale University, UK",
        degreelevel_name: "Bachelors",
        qs_ranking: 307,
        studyMode: "On campus",
        fee: undefined,
        duration: "4 years",
        course_language: "English",
        logo: "/images/university-logo.png",
      },
      {
        name: "Bachelor of Architectural Engineering (Hons) with Year Abroad",
        institute_name: "Yale University, UK",
        degreelevel_name: "Bachelors",
        qs_ranking: 307,
        studyMode: "On campus",
        fee: 14900,
        duration: "4 years",
        course_language: "English",
        logo: "",
      },
    ],
  },
  {
    label: "Master",
    value: "masters",
    courses: [],
  },
  // {
  //   label: "Cerifications",
  //   value: "certificate",
  //   courses: [],
  // },
];

export const CampusLifeTabsData: CampusLifeTabData[] = [
  {
    label: "Student life",
    value: "studentLife",
    data: {
      img: "/images/student-life.png",
      paragraph1:
        "From Fresher's Week to Graduation, you'll be meeting like-minded people throughout your time at Yale. New students are all invited to take part in Welcome Week - an exciting introduction to student life. With events such as International Night, a Student Reception, and a host of cultural and social events, you'll have plenty to do from the moment you arrive. Delivered by the Yale Students’ Union and the University, these events ensure you'll feel at home in a new city and a new environment. This is your introduction to the Yale family.",
      paragraph2:
        "Every student should feel fully supported throughout their studies. At Yale, there are a number of societies to meet new people, make friends and collaborate",
    },
  },
  {
    label: "University faculty",
    value: "universityFaculty",
    data: {},
  },
  {
    label: "Clubs and societies",
    value: "clubsAndSocieties",
    data: {},
  },
  {
    label: "Student's unions",
    value: "studentsUnion",
    data: {},
  },
];

export const BrowseCourseData: UniversityHeading = {
  title: "Browse Course at Yale",
};

export const CampuslifeSection: UniversityHeading = {
  title: "Campus life",
};

export const TrendingTopicsData: TrendingTopic[] = [
  { title: "Yale University", posts: 238 },
  { title: "Study In Yale", posts: 31 },
  { title: "Tuition Fee", posts: 28 },
  { title: "Tuition Fee", posts: 28 },
];

export const ComminutiySection: UniversityHeading = {
  title: "Community",
};
export const TrendingTopics: UniversityHeading = {
  title: "Trending topics",
};
export const exploreMoreUniversities: UniversityHeading = {
  title: "Explore more universities",
};

export interface Labels {
  type: string;
  year: string;
  ranking: string;
  fee: string;
  duration: string;
  language: string;
  viewUniveristy: string;
  viewCourses: string;
}
export interface CourseData {
  title: string;
  location: string;
  type: string;
  year: number;
  ranking: number;
  fee?: number;
  duration: string;
  language: string;
  image: string;
}

export interface UniversityCard {
  labels: Labels;
  courses: CourseData[];
  defaultImage: string;
  altImage: string;
  defaultFee: string;
}

export const universityCardData: UniversityCard = {
  defaultImage: "/images/university-logo-default.png",
  altImage: "Course Image",
  defaultFee: "To Be COnfirmed",
  labels: {
    type: "Type",
    year: "Established year",
    ranking: "TCF Ranking",
    fee: "Fee",
    duration: "Duration",
    language: "Language",
    viewUniveristy: "View University",
    viewCourses: "View courses",
  },
  courses: [
    {
      title: "Yale University",
      location: "New Haven, CT 06520, USA",
      type: "Public",
      year: 1885,
      ranking: 138,
      fee: undefined,
      duration: "4 years",
      language: "English",
      image: "/images/university-logo.png",
    },
    {
      title: "Bangor University",
      location: "New Haven, CT 06520, USA",
      type: "Public",
      year: 1885,
      ranking: 138,
      fee: 14900,
      duration: "4 years",
      language: "English",
      image: "",
    },
  ],
};
